import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";

interface SalaryData {
  expected_salary: number | null;
  market_min: number | null;
  market_median: number | null;
  market_max: number | null;
  currency: string;
  region: string;
  analysis: string;
  position_title: string;
}

export function SalaryBenchmark({ candidateId }: { candidateId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.candidateSalary(candidateId),
    queryFn: () => api.getCandidateSalary(candidateId),
  });

  const salary = data?.salary as SalaryData | null;

  if (isLoading) {
    return <p className="text-xs text-text-secondary">Loading salary data...</p>;
  }

  if (!salary || (!salary.market_median && !salary.expected_salary)) {
    return <p className="text-xs text-text-secondary">No salary data available</p>;
  }

  const fmt = (n: number | null) =>
    n != null ? `${salary.currency || "USD"} ${n.toLocaleString()}` : "—";

  // Calculate position of expected salary within market range for the bar
  const min = salary.market_min ?? 0;
  const max = salary.market_max ?? 0;
  const range = max - min;
  const expectedPct =
    salary.expected_salary && range > 0
      ? Math.max(0, Math.min(100, ((salary.expected_salary - min) / range) * 100))
      : null;

  const verdictColor =
    salary.analysis?.includes("below") ? "text-emerald-600"
    : salary.analysis?.includes("above") ? "text-rausch"
    : "text-amber-600";

  return (
    <div className="space-y-3">
      <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
        Salary benchmark
      </div>

      {/* Market range bar */}
      {salary.market_min != null && salary.market_max != null && (
        <div className="space-y-1">
          <div className="flex justify-between text-[10px] text-text-secondary">
            <span>{fmt(salary.market_min)}</span>
            <span className="font-semibold text-near-black">{fmt(salary.market_median)}</span>
            <span>{fmt(salary.market_max)}</span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-secondary">
            {/* Market range fill */}
            <div className="absolute inset-0 rounded-full bg-blue-200" />
            {/* Median marker */}
            <div
              className="absolute top-0 h-full w-0.5 bg-blue-600"
              style={{ left: "50%" }}
            />
            {/* Expected salary marker */}
            {expectedPct != null && (
              <div
                className="absolute top-0 h-full w-1 rounded-full bg-rausch"
                style={{ left: `${expectedPct}%` }}
                title={`Expected: ${fmt(salary.expected_salary)}`}
              />
            )}
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-text-secondary">Min</span>
            <span className="text-text-secondary">Median</span>
            <span className="text-text-secondary">Max</span>
          </div>
        </div>
      )}

      {/* Expected salary */}
      {salary.expected_salary != null && (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-near-black">Expected:</span>
          <span className="font-bold text-near-black">{fmt(salary.expected_salary)}</span>
        </div>
      )}

      {/* Region */}
      {salary.region && (
        <p className="text-xs text-text-secondary">Region: {salary.region}</p>
      )}

      {/* Analysis */}
      {salary.analysis && (
        <p className={`text-xs leading-[1.43] ${verdictColor}`}>{salary.analysis}</p>
      )}
    </div>
  );
}
